import { JSX, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  BookOpen,
  Calendar,
  CircleCheck,
  Landmark,
  Lightbulb,
  RotateCw,
  SlidersHorizontal,
  Tags,
} from "lucide-react";

import dicaRapidaArt from "../../assets/filters/dica-rapida.png";
import dicaRapidaArtDark from "../../assets/filters/dica-rapida-dark.png";
import useFilterOptions from "../../../core/hooks/useFilterOptions";
import useQuestionFilters from "../../../core/hooks/useQuestionFilters";
import { FilterDimension } from "../../../core/store/useQuestionFiltersStore";

import ActiveFilters from "./ActiveFilters";
import FilterActionBar from "./FilterActionBar";
import FilterChip from "./FilterChip";
import FilterChipGroup from "./FilterChipGroup";
import FilterOptionsDialog from "./FilterOptionsDialog";
import FilterOverflow from "./FilterOverflow";
import FilterRail, { FilterRailSection } from "./FilterRail";
import FilterSearch from "./FilterSearch";
import FilterSection from "./FilterSection";
import FilterStatusGroup from "./FilterStatusGroup";
import { matchesQuery, orderBySelection } from "./optionUtils";
import useCompactLayout from "./useCompactLayout";
import { SectionAccent, VISIBLE_OPTIONS, cx, overflowMode } from "./tokens";

interface SectionConfig {
  id: string;
  step: number;
  dimension: FilterDimension;
  accent: SectionAccent;
  icon: JSX.Element;
  title: string;
  description: string;
  searchPlaceholder: string;
  /** Plural noun for counts and screen-reader labels. */
  noun: string;
}

/**
 * Sections are numbered because they form a narrowing funnel — each choice
 * reduces what the next one offers (§23.4). Rail order, step order and DOM
 * order are the same order.
 */
const SECTIONS: SectionConfig[] = [
  {
    id: "filtro-ano",
    step: 1,
    dimension: "years",
    accent: "blue",
    icon: <Calendar aria-hidden="true" />,
    title: "Ano",
    description: "Selecione um ou mais anos",
    searchPlaceholder: "Buscar ano…",
    noun: "anos",
  },
  {
    id: "filtro-instituicao",
    step: 2,
    dimension: "agencies",
    accent: "cyan",
    icon: <Landmark aria-hidden="true" />,
    title: "Instituição / Banca",
    description: "Selecione uma ou mais instituições",
    searchPlaceholder: "Buscar instituição…",
    noun: "instituições",
  },
  {
    id: "filtro-materia",
    step: 3,
    dimension: "subjects",
    accent: "purple",
    icon: <BookOpen aria-hidden="true" />,
    title: "Matéria",
    description: "Selecione uma ou mais matérias",
    searchPlaceholder: "Buscar matéria…",
    noun: "matérias",
  },
  {
    id: "filtro-assunto",
    step: 4,
    dimension: "keywords",
    accent: "orange",
    icon: <Tags aria-hidden="true" />,
    title: "Assunto",
    // Deliberately domain-neutral: this list is the question tags, which cover
    // laws for a fire-service exam but equally anatomy, algorithms or case law
    // for another. Nothing here should name one field.
    description: "Selecione um ou mais assuntos",
    searchPlaceholder: "Buscar assunto…",
    noun: "assuntos",
  },
];

const STATUS_SECTION_ID = "filtro-status";

function ChipSkeleton({ count = 8 }: { count?: number }) {
  return (
    <div className="flex flex-wrap gap-2" aria-hidden="true">
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          className="h-9 animate-pulse rounded-chip bg-surface-muted"
          style={{ width: `${72 + ((index * 37) % 96)}px` }}
        />
      ))}
    </div>
  );
}

function NoMatches({ onClear }: { onClear: () => void }) {
  return (
    <div className="rounded-chip border border-dashed border-edge px-4 py-6 text-center">
      <p className="text-[13px] font-medium text-ink">Nenhum filtro encontrado</p>
      <p className="mt-1 text-xs text-ink-muted">Tente outro termo de busca.</p>
      <button
        type="button"
        onClick={onClear}
        className="mt-2 text-xs font-medium text-brand hover:underline"
      >
        Limpar busca
      </button>
    </div>
  );
}

export interface FilterPanelProps {
  /** Rendered by the drawer's own header when embedded; omit inside a drawer. */
  showHeading?: boolean;
  className?: string;
}

export default function FilterPanel({ showHeading = false, className }: FilterPanelProps) {
  const {
    draft,
    activeFilters,
    selectedCount,
    pendingChanges,
    applying,
    toggle,
    remove,
    setAnswerStatus,
    clearAll,
    apply,
  } = useQuestionFilters();

  const options = useFilterOptions();
  const isCompact = useCompactLayout();

  const [globalQuery, setGlobalQuery] = useState("");
  const [sectionQuery, setSectionQuery] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [dialogSectionId, setDialogSectionId] = useState<string | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const [activeRailId, setActiveRailId] = useState<string>(SECTIONS[0].id);

  const scrollRef = useRef<HTMLDivElement>(null);
  const globalInputRef = useRef<HTMLInputElement>(null);

  const optionsByDimension = useMemo<Record<FilterDimension, string[]>>(
    () => ({
      years: options.years,
      agencies: options.agencyNames,
      subjects: options.subjectNames,
      keywords: options.keywords,
    }),
    [options.years, options.agencyNames, options.subjectNames, options.keywords],
  );

  // Ctrl/Cmd + K is advertised on the global field, so it has to work (§12).
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        globalInputRef.current?.focus();
        globalInputRef.current?.select();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Keep the rail in step with what is actually on screen.
  useEffect(() => {
    const root = scrollRef.current;
    if (!root || globalQuery) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActiveRailId(visible[0].target.id);
      },
      { root, rootMargin: "0px 0px -70% 0px", threshold: 0 },
    );

    [...SECTIONS.map((s) => s.id), STATUS_SECTION_ID].forEach((id) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [globalQuery, options.loadingBase]);

  const scrollToSection = useCallback((id: string) => {
    setGlobalQuery("");
    setCollapsedSections((current) => ({ ...current, [id]: false }));
    setActiveRailId(id);
    window.requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);

  const railSections: FilterRailSection[] = useMemo(
    () => [
      ...SECTIONS.map((section) => ({
        id: section.id,
        label: section.title,
        icon: section.icon,
        total: optionsByDimension[section.dimension].length,
        selected: draft[section.dimension].length,
      })),
      {
        id: STATUS_SECTION_ID,
        label: "Status das questões",
        icon: <CircleCheck aria-hidden="true" />,
        total: 3,
        selected: 0,
      },
    ],
    [draft, optionsByDimension],
  );

  const globalResults = useMemo(() => {
    if (!globalQuery.trim()) return [];
    return SECTIONS.map((section) => ({
      section,
      matches: optionsByDimension[section.dimension].filter((option) =>
        matchesQuery(option, globalQuery),
      ),
    })).filter((group) => group.matches.length > 0);
  }, [globalQuery, optionsByDimension]);

  const dialogSection = SECTIONS.find((section) => section.id === dialogSectionId) ?? null;

  const renderSectionOptions = (section: SectionConfig) => {
    const all = optionsByDimension[section.dimension];
    const selected = draft[section.dimension];
    const query = sectionQuery[section.id] ?? "";

    const isKeywordSection = section.dimension === "keywords";
    if (options.loadingBase || (isKeywordSection && options.loadingKeywords)) {
      return <ChipSkeleton />;
    }

    if (isKeywordSection && options.keywordsError) {
      return (
        <div className="flex items-center gap-2 rounded-chip border border-edge px-4 py-3 text-[13px] text-ink-secondary">
          <AlertCircle className="h-4 w-4 shrink-0 text-warn-ink" aria-hidden="true" />
          <span className="flex-1">Não foi possível carregar os assuntos.</span>
          <button
            type="button"
            onClick={options.reload}
            className="inline-flex items-center gap-1 font-medium text-brand hover:underline"
          >
            <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
            Tentar novamente
          </button>
        </div>
      );
    }

    if (all.length === 0) {
      return (
        <p className="text-[13px] text-ink-muted">
          {`Nenhuma opção de ${section.noun} disponível no momento.`}
        </p>
      );
    }

    const filtered = all.filter((option) => matchesQuery(option, query));
    if (filtered.length === 0) {
      return (
        <NoMatches
          onClear={() => setSectionQuery((current) => ({ ...current, [section.id]: "" }))}
        />
      );
    }

    const ordered = orderBySelection(filtered, selected);
    const mode = overflowMode(filtered.length);
    const isExpanded = expanded[section.id] ?? false;
    const visible =
      mode === "inline" && !isExpanded ? ordered.slice(0, VISIBLE_OPTIONS) : ordered;
    const shown = mode === "modal" ? visible.slice(0, VISIBLE_OPTIONS) : visible;

    return (
      <FilterChipGroup labelledBy={`${section.id}-title`}>
        {shown.map((option) => (
          <FilterChip
            key={option}
            label={option}
            selected={selected.includes(option)}
            onToggle={() => toggle(section.dimension, option)}
          />
        ))}
      </FilterChipGroup>
    );
  };

  const renderSectionHeaderExtras = (section: SectionConfig) => {
    const all = optionsByDimension[section.dimension];
    const query = sectionQuery[section.id] ?? "";
    const filteredCount = all.filter((option) => matchesQuery(option, query)).length;
    const mode = overflowMode(filteredCount);

    // Section search appears once a section is big enough to be worth narrowing (§23.2).
    const search =
      all.length > VISIBLE_OPTIONS ? (
        <FilterSearch
          value={query}
          onChange={(value) =>
            setSectionQuery((current) => ({ ...current, [section.id]: value }))
          }
          placeholder={section.searchPlaceholder}
          ariaLabel={`Buscar em ${section.title}`}
        />
      ) : undefined;

    const overflow =
      mode === "none" ? undefined : (
        <FilterOverflow
          mode={mode}
          total={filteredCount}
          noun={section.noun}
          expanded={expanded[section.id] ?? false}
          onToggleExpand={() =>
            setExpanded((current) => ({ ...current, [section.id]: !current[section.id] }))
          }
          onViewAll={() => setDialogSectionId(section.id)}
        />
      );

    return { search, overflow };
  };

  if (options.baseError) {
    return (
      <div className={cx("grid h-full place-items-center bg-canvas p-8", className)}>
        <div className="max-w-sm text-center">
          <AlertCircle className="mx-auto h-8 w-8 text-warn-ink" aria-hidden="true" />
          <h2 className="mt-3 text-base font-semibold text-ink">
            Não foi possível carregar os filtros
          </h2>
          <p className="mt-1 text-[13px] text-ink-muted">
            Verifique sua conexão e tente novamente.
          </p>
          <button
            type="button"
            onClick={options.reload}
            className="mt-4 inline-flex h-10 items-center gap-2 rounded-input bg-brand px-5 text-sm font-semibold text-brand-contrast hover:brightness-110"
          >
            <RotateCw className="h-4 w-4" aria-hidden="true" />
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={cx("flex h-full min-h-0 flex-col bg-canvas", className)}>
      <div className="flex min-h-0 min-w-0 flex-1">
        {/* -------------------------------------------------------- rail */}
        <aside className="hidden w-72 shrink-0 flex-col gap-4 overflow-y-auto border-r border-edge bg-surface p-4 lg:flex">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] bg-select/[0.14] text-select-ink">
              <SlidersHorizontal className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <h2 className="text-base font-semibold text-ink">Filtros</h2>
              <p className="text-[13px] text-ink-muted">Refine sua busca e encontre o que precisa</p>
            </div>
          </div>

          <FilterRail
            sections={railSections}
            activeId={activeRailId}
            onSelect={scrollToSection}
          />

          <ActiveFilters
            items={activeFilters}
            onRemove={(item) => remove(item.dimension, item.value)}
            onClearAll={clearAll}
            className="sticky bottom-0"
          />

          <div className="rounded-card border border-edge bg-surface-muted p-4">
            <div className="flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-accent-orange-ink" aria-hidden="true" />
              <h3 className="text-[13px] font-semibold text-ink">Dica rápida</h3>
            </div>
            <div className="mt-1.5 flex items-end gap-2">
              <p className="min-w-0 flex-1 text-xs leading-relaxed text-ink-muted">
                Use a busca de cada seção para encontrar filtros rapidamente, ou
                <kbd className="mx-1 rounded border border-edge bg-surface px-1.5 py-0.5 font-sans text-[11px]">
                  Ctrl + K
                </kbd>
                para buscar em todos.
              </p>
              <img
                src={dicaRapidaArt}
                alt=""
                aria-hidden="true"
                className="-mb-1 -mr-1 h-[76px] w-[76px] shrink-0 select-none object-contain dark:hidden"
                draggable={false}
              />
              <img
                src={dicaRapidaArtDark}
                alt=""
                aria-hidden="true"
                className="-mb-1 -mr-1 hidden h-[76px] w-[76px] shrink-0 select-none object-contain dark:block"
                draggable={false}
              />
            </div>
          </div>
        </aside>

        {/* ----------------------------------------------------- content */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {showHeading ? (
            <div className="border-b border-edge bg-surface px-3 py-4 sm:px-5 lg:hidden">
              <h2 className="text-base font-semibold text-ink">Filtros</h2>
              <p className="text-[13px] text-ink-muted">Refine sua busca e encontre o que precisa</p>
            </div>
          ) : null}

          <div className="border-b border-edge bg-surface px-3 py-3 sm:px-5">
            <FilterSearch
              ref={globalInputRef}
              variant="global"
              value={globalQuery}
              onChange={setGlobalQuery}
              placeholder="Busca rápida em todos os filtros…"
              ariaLabel="Busca rápida em todos os filtros"
              shortcut={isCompact ? undefined : "Ctrl + K"}
            />
          </div>

          <div
            ref={scrollRef}
            className="min-h-0 min-w-0 flex-1 space-y-4 overflow-x-hidden overflow-y-auto p-3 sm:p-5"
          >
            <ActiveFilters
              variant="inline"
              items={activeFilters}
              onRemove={(item) => remove(item.dimension, item.value)}
              onClearAll={clearAll}
              className="lg:hidden"
            />

            {globalQuery.trim() ? (
              /* Global results always name the section each match belongs to (§23.2). */
              <div className="space-y-4" aria-live="polite">
                {globalResults.length === 0 ? (
                  <div className="rounded-card border border-edge bg-surface p-8 text-center">
                    <p className="text-sm font-medium text-ink">Nenhum filtro encontrado</p>
                    <p className="mt-1 text-[13px] text-ink-muted">
                      {`Nada corresponde a “${globalQuery}”. Tente outro termo.`}
                    </p>
                  </div>
                ) : (
                  globalResults.map(({ section, matches }) => (
                    <div
                      key={section.id}
                      className="rounded-card border border-edge bg-surface p-4 shadow-panel sm:p-5"
                    >
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <h3 className="text-[13px] font-semibold uppercase tracking-wide text-ink-muted">
                          {section.title}
                        </h3>
                        <span className="text-xs tabular-nums text-ink-muted">
                          {`${matches.length} de ${optionsByDimension[section.dimension].length}`}
                        </span>
                      </div>
                      <FilterChipGroup ariaLabel={`Resultados em ${section.title}`}>
                        {matches.slice(0, 24).map((option) => (
                          <FilterChip
                            key={option}
                            label={option}
                            selected={draft[section.dimension].includes(option)}
                            onToggle={() => toggle(section.dimension, option)}
                          />
                        ))}
                      </FilterChipGroup>
                    </div>
                  ))
                )}
              </div>
            ) : (
              <>
                {SECTIONS.map((section) => {
                  const { search, overflow } = renderSectionHeaderExtras(section);
                  const open = !(collapsedSections[section.id] ?? false);

                  return (
                    <FilterSection
                      key={section.id}
                      id={section.id}
                      step={section.step}
                      icon={section.icon}
                      accent={section.accent}
                      title={section.title}
                      description={section.description}
                      search={search}
                      headerRight={overflow}
                      collapsible={isCompact}
                      open={open}
                      selectedCount={draft[section.dimension].length}
                      onToggleOpen={() =>
                        setCollapsedSections((current) => ({
                          ...current,
                          [section.id]: !(current[section.id] ?? false),
                        }))
                      }
                    >
                      {renderSectionOptions(section)}
                    </FilterSection>
                  );
                })}

                <FilterSection
                  id={STATUS_SECTION_ID}
                  step={5}
                  icon={<CircleCheck aria-hidden="true" />}
                  accent="green"
                  title="Status das questões"
                  description="Filtre por status"
                  collapsible={isCompact}
                  open={!(collapsedSections[STATUS_SECTION_ID] ?? false)}
                  onToggleOpen={() =>
                    setCollapsedSections((current) => ({
                      ...current,
                      [STATUS_SECTION_ID]: !(current[STATUS_SECTION_ID] ?? false),
                    }))
                  }
                >
                  <FilterStatusGroup
                    value={draft.answerStatus}
                    onChange={setAnswerStatus}
                    labelledBy={`${STATUS_SECTION_ID}-title`}
                  />
                </FilterSection>
              </>
            )}
          </div>

          <FilterActionBar
            onClear={clearAll}
            onApply={apply}
            applying={applying}
            selectedCount={selectedCount}
            pendingChanges={pendingChanges}
          />
        </div>
      </div>

      {dialogSection ? (
        <FilterOptionsDialog
          open
          title={dialogSection.title}
          noun={dialogSection.noun}
          options={optionsByDimension[dialogSection.dimension]}
          selected={draft[dialogSection.dimension]}
          onToggle={(value) => toggle(dialogSection.dimension, value)}
          onClose={() => setDialogSectionId(null)}
          searchPlaceholder={dialogSection.searchPlaceholder}
        />
      ) : null}
    </div>
  );
}
